import React, { useEffect, useState } from 'react';
import { shopifyAPI } from '../services/api';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useTenantUrl } from '../hooks/useTenantUrl';

// Date preset helpers
const getDatePreset = (preset: string): { start: string; end: string } => {
  const now = new Date();
  switch (preset) {
    case 'this_month':
      return {
        start: format(startOfMonth(now), 'yyyy-MM-dd'),
        end: format(endOfMonth(now), 'yyyy-MM-dd'),
      };
    case 'last_month':
      const lastMonth = subMonths(now, 1);
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
      };
    case 'this_year':
      return {
        start: format(startOfYear(now), 'yyyy-MM-dd'),
        end: format(endOfYear(now), 'yyyy-MM-dd'),
      };
    case 'last_year':
      const lastYear = subYears(now, 1);
      return {
        start: format(startOfYear(lastYear), 'yyyy-MM-dd'),
        end: format(endOfYear(lastYear), 'yyyy-MM-dd'),
      };
    default:
      return { start: '', end: '' };
  }
};

const Orders: React.FC = () => {
  const { tenantNavigate } = useTenantUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  
  // Initialize filters and page from URL, default to last month if no dates specified
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const defaultDates = getDatePreset('last_month');
  const [filters, setFilters] = useState({
    start_date: searchParams.get('start_date') || defaultDates.start,
    end_date: searchParams.get('end_date') || defaultDates.end,
    financial_status: searchParams.get('financial_status') || '',
    fulfillment_status: searchParams.get('fulfillment_status') || '',
    search: searchParams.get('search') || '',
    payment_method_name: searchParams.get('payment_method_name') || '',
    paid_date_start: searchParams.get('paid_date_start') || '',
    paid_date_end: searchParams.get('paid_date_end') || '',
  });
  const [datePreset, setDatePreset] = useState('last_month');

  // Fetch payment methods on mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await shopifyAPI.getPaymentMethods();
        setPaymentMethods(response.data.data);
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
      }
    };
    fetchPaymentMethods();
  }, []);

  // Fetch orders when page changes
  useEffect(() => {
    updateURLParams(filters, currentPage);
    fetchOrders();
  }, [currentPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };
      if (filters.start_date) params.start_date = filters.start_date;
      
      // Make end date inclusive by setting to end of day in UTC
      // Database stores timestamps in UTC, so we filter by UTC day
      if (filters.end_date) {
        params.end_date = filters.end_date + 'T23:59:59.999Z';
      }
      
      if (filters.financial_status) params.financial_status = filters.financial_status;
      if (filters.fulfillment_status) params.fulfillment_status = filters.fulfillment_status;
      if (filters.search) params.search = filters.search;
      if (filters.payment_method_name) params.payment_method_name = filters.payment_method_name;
      if (filters.paid_date_start) params.paid_date_start = filters.paid_date_start;
      if (filters.paid_date_end) params.paid_date_end = filters.paid_date_end;

      const response = await shopifyAPI.getOrders(params);
      setOrders(response.data.data);
      
      // Calculate total pages from total count
      const count = response.data.summary?.total_count || 0;
      setTotalCount(count);
      setTotalPages(Math.ceil(count / itemsPerPage));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = (newFilters: typeof filters, page: number = currentPage) => {
    const params: any = {};
    if (page > 1) params.page = page.toString();
    if (newFilters.start_date) params.start_date = newFilters.start_date;
    if (newFilters.end_date) params.end_date = newFilters.end_date;
    if (newFilters.financial_status) params.financial_status = newFilters.financial_status;
    if (newFilters.fulfillment_status) params.fulfillment_status = newFilters.fulfillment_status;
    if (newFilters.search) params.search = newFilters.search;
    if (newFilters.payment_method_name) params.payment_method_name = newFilters.payment_method_name;
    if (newFilters.paid_date_start) params.paid_date_start = newFilters.paid_date_start;
    if (newFilters.paid_date_end) params.paid_date_end = newFilters.paid_date_end;
    
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear preset when manually editing dates
    if (name === 'start_date' || name === 'end_date') {
      setDatePreset('custom');
    }
  };

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const preset = e.target.value;
    setDatePreset(preset);
    if (preset !== 'custom') {
      const dates = getDatePreset(preset);
      setFilters(prev => ({
        ...prev,
        start_date: dates.start,
        end_date: dates.end,
      }));
    }
  };

  const handleApplyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    updateURLParams(filters, 1);
    fetchOrders();
  };

  const handleClearFilters = () => {
    const defaultDates = getDatePreset('last_month');
    const clearedFilters = {
      start_date: defaultDates.start,
      end_date: defaultDates.end,
      financial_status: '',
      fulfillment_status: '',
      search: '',
      payment_method_name: '',
      paid_date_start: '',
      paid_date_end: '',
    };
    setFilters(clearedFilters);
    setDatePreset('last_month');
    setCurrentPage(1);
    updateURLParams(clearedFilters, 1);
    fetchOrders();
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleViewOrder = (orderId: number) => {
    tenantNavigate(`/orders/${orderId}`);
  };

  const handleExport = () => {
    const params: any = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date + 'T23:59:59.999Z';
    if (filters.financial_status) params.financial_status = filters.financial_status;
    if (filters.fulfillment_status) params.fulfillment_status = filters.fulfillment_status;
    if (filters.search) params.search = filters.search;
    if (filters.payment_method_name) params.payment_method_name = filters.payment_method_name;
    if (filters.paid_date_start) params.paid_date_start = filters.paid_date_start;
    if (filters.paid_date_end) params.paid_date_end = filters.paid_date_end;
    shopifyAPI.exportOrders(params);
  };

  if (loading) {
    return <div className="loading">Loading orders...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>Orders</h1>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Filters</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>Preset:</span>
            <select
              id="date_preset"
              name="date_preset"
              className="form-control"
              value={datePreset}
              onChange={handlePresetChange}
              style={{ width: 'auto', minWidth: '130px' }}
            >
              <option value="custom">Custom</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_year">This Year</option>
              <option value="last_year">Last Year</option>
            </select>
          </div>
        </div>
        <form onSubmit={handleApplyFilters}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label htmlFor="start_date">Start Date</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                className="form-control"
                value={filters.start_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">End Date</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                className="form-control"
                value={filters.end_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="search">Search (Order # or Email)</label>
              <input
                type="text"
                id="search"
                name="search"
                className="form-control"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Enter order number or email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="financial_status">Financial Status</label>
              <select
                id="financial_status"
                name="financial_status"
                className="form-control"
                value={filters.financial_status}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="authorized">Authorized</option>
                <option value="partially_paid">Partially Paid</option>
                <option value="paid">Paid</option>
                <option value="partially_refunded">Partially Refunded</option>
                <option value="refunded">Refunded</option>
                <option value="voided">Voided</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="fulfillment_status">Fulfillment Status</label>
              <select
                id="fulfillment_status"
                name="fulfillment_status"
                className="form-control"
                value={filters.fulfillment_status}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="partial">Partial</option>
                <option value="unfulfilled">Unfulfilled</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="payment_method_name">Payment Method</label>
              <select
                id="payment_method_name"
                name="payment_method_name"
                className="form-control"
                value={filters.payment_method_name}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {paymentMethods.map((method) => (
                  <option key={method.name} value={method.name}>
                    {method.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paid_date_start">Paid Date From</label>
              <input
                type="date"
                id="paid_date_start"
                name="paid_date_start"
                className="form-control"
                value={filters.paid_date_start}
                onChange={handleFilterChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="paid_date_end">Paid Date To</label>
              <input
                type="date"
                id="paid_date_end"
                name="paid_date_end"
                className="form-control"
                value={filters.paid_date_end}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary">
                Apply Filters
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleExport}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              📥 Export CSV
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Orders Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Orders {totalCount > 0 && <span style={{ fontWeight: 400, fontSize: '1rem', color: '#718096' }}>({totalCount.toLocaleString()} total)</span>}</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <span style={{ color: '#718096', fontSize: '0.9rem' }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                style={{ padding: '0.5rem 1rem' }}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                style={{ padding: '0.5rem 1rem' }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Total Amount</th>
                <th>Financial Status</th>
                <th>Fulfillment Status</th>
                <th>Transactions</th>
                <th>Payments</th>
                <th>Refunds</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '2rem' }}>
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {order.name || `#${order.order_number}`}
                      </span>
                    </td>
                    <td>{format(new Date(order.order_date), 'MMM dd, yyyy HH:mm')}</td>
                    <td>
                      <div style={{ fontSize: '0.9rem' }}>
                        {order.customer_email || 'N/A'}
                      </div>
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      ${Number(order.total_amount).toFixed(2)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span className={`status-badge ${
                          order.financial_status === 'paid' ? 'status-reconciled' :
                          order.financial_status === 'refunded' ? 'status-discrepancy' :
                          'status-pending'
                        }`}>
                          {order.financial_status}
                        </span>
                        {order.paid_date && (
                          <span style={{ fontSize: '0.75rem', color: '#48bb78' }}>
                            {format(new Date(order.paid_date), 'MMM dd, yyyy')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span className={`status-badge ${
                          order.fulfillment_status === 'fulfilled' ? 'status-reconciled' :
                          order.fulfillment_status === 'partial' ? 'status-pending' :
                          'status-unmapped'
                        }`}>
                          {order.fulfillment_status || 'unfulfilled'}
                        </span>
                        {order.fulfillment_date && (
                          <span style={{ fontSize: '0.75rem', color: '#48bb78' }}>
                            {format(new Date(order.fulfillment_date), 'MMM dd, yyyy')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {order.transaction_count || 0}
                    </td>
                    <td style={{ color: '#48bb78' }}>
                      ${Number(order.total_payments || 0).toFixed(2)}
                    </td>
                    <td style={{ color: '#f56565' }}>
                      ${Number(order.total_refunds || 0).toFixed(2)}
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                        onClick={() => handleViewOrder(order.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Orders;

